import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import useMatterSelection from '@/hooks/useMatterSelection';
import { cn } from '@/lib/utils';

import { DomainEventsFeed } from '../domain-events-v2';
import { EventFeedSkeleton } from '../domain-events-v2/EventFeedSkeleton';
import { NotesPanel } from '../notes-panel';

export type NotesActivityFilter = 'notes' | 'activity';

interface NotesActivityPanelProps {
  defaultFilter?: NotesActivityFilter;
}

const FILTER_OPTIONS: { key: NotesActivityFilter; label: string }[] = [
  { key: 'notes', label: 'Notes' },
  { key: 'activity', label: 'Activity' },
];

export const NotesActivityPanel: React.FC<NotesActivityPanelProps> = ({
  defaultFilter = 'notes',
}) => {
  const { selectedMatterData, selectedMatterLoading } = useMatterSelection();
  const [activeFilter, setActiveFilter] =
    useState<NotesActivityFilter>(defaultFilter);

  const [searchParams] = useSearchParams();

  // Sync with defaultFilter prop when it changes (e.g. deep link while panel is mounted)
  useEffect(() => {
    setActiveFilter(defaultFilter);
  }, [defaultFilter]);

  // Respond to item-level deep-link params directly so the tab switches
  // even when defaultFilter hasn't changed (e.g. user is on Activity, deep-links to a note)
  useEffect(() => {
    const noteId = searchParams.get('note_id');
    const eventId = searchParams.get('event_id');
    if (noteId) {
      setActiveFilter('notes');
    } else if (eventId) {
      setActiveFilter('activity');
    }
  }, [searchParams]);

  if (selectedMatterLoading) {
    return <EventFeedSkeleton />;
  }

  if (!selectedMatterData?.id) {
    return null;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Segmented control */}
      <div className="px-3 pt-2.5 pb-1.5">
        <div className="flex rounded-lg bg-zinc-100 p-0.5">
          {FILTER_OPTIONS.map((opt) => {
            const isActive = activeFilter === opt.key;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setActiveFilter(opt.key)}
                className={cn(
                  'flex-1 text-[11px] font-medium py-1.5 rounded-md transition-all duration-150',
                  isActive
                    ? 'bg-white text-zinc-900 shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-700',
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Panel content */}
      <div className="flex-1 overflow-hidden">
        {activeFilter === 'notes' ? (
          <NotesPanel matterId={selectedMatterData.id} />
        ) : (
          <DomainEventsFeed matterId={selectedMatterData.id} />
        )}
      </div>
    </div>
  );
};
